import { createSlice, createAsyncThunk, PayloadAction } from '@reduxjs/toolkit';
import NotificationService, { Notification } from '../../services/notifications';
import { safeArray } from '../../utils/money';

interface NotificationState {
  notifications: {
    data: Notification[];
    meta: any;
    loading: boolean;
  };
  currentNotification: Notification | null;
  unreadCount: number;
  markingAsRead: boolean;
  error: string | null;
}

const initialState: NotificationState = {
  notifications: {
    data: [],
    meta: null,
    loading: false,
  },
  currentNotification: null,
  unreadCount: 0,
  markingAsRead: false,
  error: null,
};

export const fetchNotifications = createAsyncThunk(
  'notification/fetchNotifications',
  async (
    params: {
      page?: number;
      limit?: number;
      filters?: { is_read?: boolean; type?: string };
    },
    { rejectWithValue }
  ) => {
    try {
      return await NotificationService.listNotifications(
        params.page,
        params.limit,
        params.filters
      );
    } catch (error: any) {
      return rejectWithValue(
        error.response?.data?.message || 'Failed to fetch notifications'
      );
    }
  }
);

export const fetchNotificationDetail = createAsyncThunk(
  'notification/fetchNotificationDetail',
  async (id: string, { rejectWithValue }) => {
    try {
      return await NotificationService.getNotification(id);
    } catch (error: any) {
      return rejectWithValue(
        error.response?.data?.message || 'Failed to fetch notification'
      );
    }
  }
);

export const markAsRead = createAsyncThunk(
  'notification/markAsRead',
  async (id: string, { rejectWithValue }) => {
    try {
      await NotificationService.markAsRead(id);
      return id;
    } catch (error: any) {
      return rejectWithValue(
        error.response?.data?.message || 'Failed to mark as read'
      );
    }
  }
);

export const markAllAsRead = createAsyncThunk(
  'notification/markAllAsRead',
  async (_, { rejectWithValue }) => {
    try {
      await NotificationService.markAllAsRead();
      return true;
    } catch (error: any) {
      return rejectWithValue(
        error.response?.data?.message || 'Failed to mark all as read'
      );
    }
  }
);

export const fetchUnreadCount = createAsyncThunk(
  'notification/fetchUnreadCount',
  async (_, { rejectWithValue }) => {
    try {
      return await NotificationService.getUnreadCount();
    } catch (error: any) {
      return rejectWithValue(
        error.response?.data?.message || 'Failed to fetch unread count'
      );
    }
  }
);

// Note: Delete notification not supported by backend
// export const deleteNotification = createAsyncThunk(
//   'notification/deleteNotification',
//   async (id: string, { rejectWithValue }) => {
//     try {
//       await NotificationService.deleteNotification(id);
//       return id;
//     } catch (error: any) {
//       return rejectWithValue(
//         error.response?.data?.message || 'Failed to delete notification'
//       );
//     }
//   }
// );

const notificationSlice = createSlice({
  name: 'notification',
  initialState,
  reducers: {
    clearError: (state) => {
      state.error = null;
    },
    clearCurrentNotification: (state) => {
      state.currentNotification = null;
    },
    decrementUnreadCount: (state) => {
      if (state.unreadCount > 0) {
        state.unreadCount -= 1;
      }
    },
  },
  extraReducers: (builder) => {
    // Fetch Notifications
    builder
      .addCase(fetchNotifications.pending, (state) => {
        state.notifications.loading = true;
        state.error = null;
      })
      .addCase(
        fetchNotifications.fulfilled,
        (state, action: PayloadAction<{ data: Notification[]; meta: any }>) => {
          state.notifications.loading = false;
          state.notifications.data = safeArray<Notification>(action.payload.data);
          state.notifications.meta = action.payload.meta;
        }
      )
      .addCase(fetchNotifications.rejected, (state, action) => {
        state.notifications.loading = false;
        state.error = action.payload as string;
      });

    // Fetch Notification Detail
    builder
      .addCase(fetchNotificationDetail.pending, (state) => {
        state.notifications.loading = true;
      })
      .addCase(
        fetchNotificationDetail.fulfilled,
        (state, action: PayloadAction<Notification>) => {
          state.notifications.loading = false;
          state.currentNotification = action.payload;
        }
      )
      .addCase(fetchNotificationDetail.rejected, (state, action) => {
        state.notifications.loading = false;
        state.error = action.payload as string;
      });

    // Mark As Read
    builder
      .addCase(markAsRead.pending, (state) => {
        state.markingAsRead = true;
        state.error = null;
      })
      .addCase(markAsRead.fulfilled, (state, action: PayloadAction<string>) => {
        state.markingAsRead = false;
        // Update notification in list
        const notification = state.notifications.data.find(
          (n) => n.id === action.payload
        );
        if (notification && !notification.is_read) {
          notification.is_read = true;
          notification.read_at = new Date().toISOString();
          if (state.unreadCount > 0) {
            state.unreadCount -= 1;
          }
        }
        // Update current notification if it matches
        if (state.currentNotification?.id === action.payload) {
          state.currentNotification.is_read = true;
          state.currentNotification.read_at = new Date().toISOString();
        }
      })
      .addCase(markAsRead.rejected, (state, action) => {
        state.markingAsRead = false;
        state.error = action.payload as string;
      });

    // Mark All As Read
    builder
      .addCase(markAllAsRead.pending, (state) => {
        state.markingAsRead = true;
        state.error = null;
      })
      .addCase(markAllAsRead.fulfilled, (state) => {
        state.markingAsRead = false;
        // Update all notifications
        state.notifications.data.forEach((notification) => {
          if (!notification.is_read) {
            notification.is_read = true;
            notification.read_at = new Date().toISOString();
          }
        });
        state.unreadCount = 0;
      })
      .addCase(markAllAsRead.rejected, (state, action) => {
        state.markingAsRead = false;
        state.error = action.payload as string;
      });

    // Fetch Unread Count
    builder
      .addCase(fetchUnreadCount.pending, (state) => {
        // Don't set loading state for background count fetches
      })
      .addCase(fetchUnreadCount.fulfilled, (state, action: PayloadAction<number>) => {
        state.unreadCount = action.payload;
      })
      .addCase(fetchUnreadCount.rejected, (state, action) => {
        state.error = action.payload as string;
      });

    // Delete Notification - Not supported by backend
    // builder
    //   .addCase(deleteNotification.pending, (state) => {
    //     state.markingAsRead = true;
    //   })
    //   .addCase(deleteNotification.fulfilled, (state, action: PayloadAction<string>) => {
    //     state.markingAsRead = false;
    //     const notification = state.notifications.data.find(
    //       (n) => n.id === action.payload
    //     );
    //     if (notification && !notification.is_read) {
    //       if (state.unreadCount > 0) {
    //         state.unreadCount -= 1;
    //       }
    //     }
    //     state.notifications.data = state.notifications.data.filter(
    //       (n) => n.id !== action.payload
    //     );
    //     if (state.currentNotification?.id === action.payload) {
    //       state.currentNotification = null;
    //     }
    //   })
    //   .addCase(deleteNotification.rejected, (state, action) => {
    //     state.markingAsRead = false;
    //     state.error = action.payload as string;
    //   });
  },
});

export const { clearError, clearCurrentNotification, decrementUnreadCount } =
  notificationSlice.actions;

export default notificationSlice.reducer;
